import {
    Button,
    Card,
    Grid,
    Image,
    Input,
    Modal,
} from '@design-systems/canvas-ui'
import './item-search.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MPMLayout } from 'components/layout'
import { BackButton } from 'components/button/back-button'
import { clearLocationParam } from 'utils/helpers'
import {
    stripInitialTAndLeadingZeroes,
    convertToDisplayFriendlyDateString,
} from 'utils/item-search'
import { decodeHtmlEntities } from 'utils/helpers'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useItemDetails } from 'hooks/item-details/useItemDetails'
import { Container } from 'components/container/Container'
import { CardSlider } from './card-slider'
import { messages } from './messages'
import { ErrorAlert, ErrorFallback } from 'components/error-fallback'
import useCountdown from 'hooks/utils/useCountdown'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ItemSearchChart } from './item-search-chart'
import { BasicPlaceholder } from 'components/placeholder'
import BarcodeScanner from './barcode-scanner'
import Icon, { ScanIcon } from '@design-systems/react-icons'
import { useDeviceInfo } from 'hooks/utils/useDeviceInfo'
import { ErrorBoundary } from 'react-error-boundary'
import { useLogClick } from 'utils/analytics'

export const ItemDetailsPage = () => {
    const { params, setParams, currentLocation, t } = useEssentials()
    const { itemSearch } = TRANSLATION_KEYS
    const isFromLink = params.has('fromLink')

    window.onpopstate = (_e) => {
        clearLocationParam({ params, setParams })
    }

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(itemSearch.title)}
                />
            )}
        >
            <MPMLayout
                heading={t(itemSearch.title)}
                breadcrumbsEnabled={false}
                isMenuOverride={isFromLink}
                menuOverride={isFromLink ? <BackButton /> : undefined}
            >
                <ItemDetails key={currentLocation} />
            </MPMLayout>
        </ErrorBoundary>
    )
}

const ItemDetails = () => {
    const {
        t,
        currentLocation,
        env,
        session,
        logout,
        userAgent,
        params,
        setParams,
    } = useEssentials()
    const isFromLink = params.has('fromLink')
    const [itemId, setItemId] = useState('')
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [filters, setFilters] = useState({ location_id: '', item_id: '' })
    const [isOpen, setIsOpen] = useState(false)
    const [isScannerActive, setIsScannerActive] = useState(false)
    const [isProcessingScan, setIsProcessingScan] = useState(false)
    const simpleLocation = stripInitialTAndLeadingZeroes(currentLocation)
    const { isZebraDevice } = useDeviceInfo(userAgent)
    const [keyboardSuppressed, setKeyboardSuppressed] = useState(!isZebraDevice)
    const MIN_BARCODE_LENGTH = 12
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [inputFocused, setInputFocused] = useState(false)
    const hasInitializedFromUrl = useRef(false)
    const { logClick } = useLogClick('item search')
    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            logClick('search item')
            setFilters({ location_id: simpleLocation, item_id: itemId })
            setIsSubmitted(true)
            const currentParams = Object.fromEntries(params.entries())
            setParams({ ...currentParams, itemId })
        },
        [simpleLocation, itemId, setParams, params, logClick],
    )
    const handleScan = (value: string) => {
        let filteredValue = value.replace(/[^0-9-]/g, '')
        // Remove leading zero from UPC barcodes (EAN-13 to UPC-A conversion)
        if (filteredValue.length === 13 && filteredValue.startsWith('0')) {
            filteredValue = filteredValue.substring(1)
        }
        setItemId(filteredValue)
        setIsScannerActive(false)
        if (filteredValue.length >= MIN_BARCODE_LENGTH) {
            setTimeout(() => {
                const formElement = document.getElementById(
                    'item-search-form',
                ) as HTMLFormElement | null
                formElement?.requestSubmit()
            }, 100)
        }
    }
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Prevent new scan input while processing a scan
        if (isProcessingScan && isZebraDevice) {
            return
        }

        const filteredValue = e.target.value.replace(/[^0-9-]/g, '')
        setItemId(filteredValue)

        // For Zebra devices, auto-submit when barcode length is reached
        if (isZebraDevice && filteredValue.length >= MIN_BARCODE_LENGTH) {
            setIsProcessingScan(true)
            setTimeout(() => {
                const formElement = document.getElementById(
                    'item-search-form',
                ) as HTMLFormElement | null
                formElement?.requestSubmit()
            }, 100)
        }
    }

    const handleInputTap = () => {
        setKeyboardSuppressed(false)
    }

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
    }, [])

    useEffect(() => {
        const urlItemId = params.get('itemId')
        if (urlItemId) {
            if (
                !hasInitializedFromUrl.current ||
                urlItemId !== filters.item_id
            ) {
                hasInitializedFromUrl.current = true
                setItemId(urlItemId)
                setFilters({ location_id: simpleLocation, item_id: urlItemId })
                setIsSubmitted(true)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, simpleLocation]) // Intentionally omitting filters.item_id to prevent circular dependency

    useEffect(() => {
        return () => {
            setTimeout(() => {
                if (!window.location.pathname.includes('/item-search')) {
                    const currentParams = new URLSearchParams(
                        window.location.search,
                    )
                    if (
                        currentParams.has('itemId') ||
                        currentParams.has('fromLink')
                    ) {
                        currentParams.delete('itemId')
                        currentParams.delete('fromLink')
                        const paramsObject = Object.fromEntries(
                            currentParams.entries(),
                        )
                        setParams(paramsObject)
                    }
                }
            }, 0)
        }
    }, [setParams])

    useEffect(() => {
        if (!isZebraDevice) return

        const inputElement = document.getElementById(
            'item-id-search',
        ) as HTMLInputElement | null

        if (!inputElement) return

        const observer = new MutationObserver(() => {
            const newValue = inputElement.value.replace(/[^0-9-]/g, '')
            setItemId(newValue)

            if (newValue.length >= MIN_BARCODE_LENGTH) {
                const formElement = document.getElementById(
                    'item-search-form',
                ) as HTMLFormElement | null
                formElement?.requestSubmit()
            }
        })

        observer.observe(inputElement, {
            attributes: true,
            attributeFilter: ['value'],
        })

        return () => {
            observer.disconnect()
        }
    }, [isZebraDevice])

    const { itemSearch } = TRANSLATION_KEYS
    const {
        searchLabel,
        searchInputPlaceholder,
        searchInputPlaceholderZebra,
        searchButtonText,
        dpciLabel,
        dpciAriaLabel,
        location,
        onHandRow,
        onWithdrawalRow,
        lastAuditedRow,
        lastReceivedRow,
        lastSoldRow,
        unitsSold,
        sales,
        store,
        district,
        company,
        averageSalesCardTitle,
        totalSalesCardTitle,
        itemNotFound,
    } = itemSearch

    const { data, isLoading, isError, error, refetch } = useItemDetails({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        filters,
    })

    useEffect(() => {
        // Reset processing flag whenever loading completes
        if (!isLoading && isSubmitted) {
            setIsProcessingScan(false)
            setItemId('')
            setIsScannerActive(false)
        }
    }, [isLoading, isSubmitted, data])

    const count = useCountdown(5, logout, error as Error)

    if (isError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    if (!isLoading && isSubmitted && !data?.item_details) {
        return (
            <>
                {!isFromLink && (
                    <div className="hc8-pt-6x">
                        <form
                            id="item-search-form"
                            onSubmit={handleSubmit}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    width: '100%',
                                }}
                            >
                                <Input.Label htmlFor="item-id-search">
                                    {t(searchLabel)}
                                </Input.Label>
                                <Input.Text
                                    type="text"
                                    id="item-id-search"
                                    value={itemId}
                                    placeholder={
                                        isZebraDevice && !inputFocused
                                            ? t(searchInputPlaceholderZebra)
                                            : t(searchInputPlaceholder)
                                    }
                                    ref={inputRef}
                                    inputMode={
                                        keyboardSuppressed ? 'none' : 'numeric'
                                    }
                                    onChange={handleInputChange}
                                    onClick={handleInputTap}
                                    onFocus={() => setInputFocused(true)}
                                    onBlur={() => setInputFocused(false)}
                                />

                                {!isZebraDevice && (
                                    <Button
                                        style={{
                                            position: 'absolute',
                                            top: '71%',
                                            right: '2px',
                                            transform: 'translateY(-50%)',
                                            padding: '0',
                                            height: '28px',
                                            width: '28px',
                                        }}
                                        onClick={() => {
                                            logClick('open barcode scanner')
                                            setIsScannerActive(true)
                                        }}
                                        isDisabled={isScannerActive}
                                        aria-label={t(
                                            itemSearch.scanButtonAriaLabel,
                                        )}
                                        isIconOnly
                                        variant="ghost"
                                    >
                                        <Icon svg={ScanIcon} aria-hidden />
                                    </Button>
                                )}
                            </div>

                            <Button
                                style={{ marginTop: '21px' }}
                                id="item-search-button"
                                isDisabled={isLoading && isSubmitted}
                                type="submit"
                                variant="primary"
                            >
                                {t(searchButtonText)}
                            </Button>
                        </form>
                        <BarcodeScanner
                            isScannerActive={isScannerActive}
                            onScan={handleScan}
                            onClose={() => setIsScannerActive(false)}
                        />
                    </div>
                )}
                <div className="hc8-ma-3x" id="item-not-found">
                    {t(itemNotFound)}
                </div>
            </>
        )
    }

    const {
        item_details: {
            item_info: {
                dpci = '',
                digital_assets: { base_url = '', primary_image = '' } = {},
                product_description = '',
                sales_floor_qty = 0,
                backroom_floor_qty = 0,
                on_hand_inventory = 0,
                on_withdrawal_inventory = 0,
                placements = [{}],
                last_audited_date = null,
                last_received_date = null,
                last_sold_date = null,
            } = {},
            sales_history: {
                sales_turnover: {
                    store_sales_quantity = 0,
                    store_sale_amount = 0,
                    district_total_sale_amount = 0,
                    district_total_sale_quantity = 0,
                    company_total_sale_amount = 0,
                    company_total_sale_quantity = 0,
                } = {},
                sales_average: {
                    district_average_quantity = 0,
                    district_average_amount = 0,
                    company_average_quantity = 0,
                    company_average_amount = 0,
                } = {},
            } = {
                sales_turnover: {},
                sales_average: {},
            },
        } = {
            item_info: {},
            sales_history: {
                sales_turnover: {},
                sales_average: {},
            },
        },
    } = data || {
        item_details: {
            item_info: {},
            sales_history: {
                sales_turnover: {},
                sales_average: {},
            },
        },
    }

    const decodedDescription = (() => {
        try {
            if (!product_description || product_description.trim() === '') {
                return 'No description available'
            }

            return decodeHtmlEntities(product_description)
        } catch {
            return 'No description available'
        }
    })()

    const formattedLastAuditedDate =
        convertToDisplayFriendlyDateString(last_audited_date)

    const formattedLastReceivedDate =
        convertToDisplayFriendlyDateString(last_received_date)

    const formattedLastSoldDate =
        convertToDisplayFriendlyDateString(last_sold_date)

    const onHandQty = Math.max(0, Number(on_hand_inventory) || 0)

    const onWayQty = Math.max(0, Number(on_withdrawal_inventory) || 0)

    const floorQty = Math.max(0, Number(sales_floor_qty) || 0)

    const backQty = Math.max(0, Number(backroom_floor_qty) || 0)
    const displayedDpci = dpci || filters.item_id
    const displayedDpciText = displayedDpci
        ? `${t(dpciLabel)} ${displayedDpci}`
        : ''

    return (
        <>
            {!isFromLink && (
                <div>
                    <form
                        id="item-search-form"
                        onSubmit={handleSubmit}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <div
                            style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: '100%',
                            }}
                        >
                            <Input.Label htmlFor="item-id-search">
                                {t(searchLabel)}
                            </Input.Label>
                            <Input.Text
                                type="text"
                                id="item-id-search"
                                value={itemId}
                                placeholder={
                                    isZebraDevice && !inputFocused
                                        ? t(searchInputPlaceholderZebra)
                                        : t(searchInputPlaceholder)
                                }
                                ref={inputRef}
                                inputMode={
                                    keyboardSuppressed ? 'none' : 'numeric'
                                }
                                onChange={handleInputChange}
                                onClick={handleInputTap}
                                onFocus={() => setInputFocused(true)}
                                onBlur={() => setInputFocused(false)}
                            />

                            {!isZebraDevice && (
                                <Button
                                    style={{
                                        position: 'absolute',
                                        top: '71%',
                                        right: '2px',
                                        transform: 'translateY(-50%)',
                                        padding: '0',
                                        height: '28px',
                                        width: '28px',
                                    }}
                                    onClick={() => {
                                        logClick('open barcode scanner')
                                        setIsScannerActive(true)
                                    }}
                                    aria-label={t(
                                        itemSearch.scanButtonAriaLabel,
                                    )}
                                    isIconOnly
                                    variant="ghost"
                                >
                                    <Icon svg={ScanIcon} aria-hidden />
                                </Button>
                            )}
                        </div>

                        <Button
                            style={{ marginTop: '21px' }}
                            id="item-search-button"
                            isDisabled={isLoading && isSubmitted}
                            type="submit"
                            variant="primary"
                        >
                            {t(searchButtonText)}
                        </Button>
                    </form>
                    <BarcodeScanner
                        isScannerActive={isScannerActive}
                        onScan={handleScan}
                        onClose={() => setIsScannerActive(false)}
                    />
                </div>
            )}
            {isLoading && isSubmitted && (
                <div
                    className="placeholder-container hc8-mt-3x"
                    data-testid="placeholder"
                >
                    <BasicPlaceholder type="table" rows={10} columns={1} />
                </div>
            )}
            {data && !isLoading && isSubmitted && !isScannerActive && (
                <Container>
                    <div className="hc8-mt-3x hc8-mh-3x">
                        <div
                            className="hc8-pr-6x"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <div className="item-image-and-dpci">
                                <Image
                                    src={`${base_url}${primary_image}`}
                                    alt={decodedDescription}
                                    height="88px"
                                    onClick={() => {
                                        logClick('open item image modal')
                                        setIsOpen((p) => !p)
                                    }}
                                    id="small-item-image"
                                />
                                {displayedDpci && (
                                    <p
                                        className="item-dpci"
                                        aria-label={`${t(dpciAriaLabel)} ${displayedDpci}`}
                                    >
                                        {displayedDpciText}
                                    </p>
                                )}
                            </div>
                            <h2 className="hc8-ml-3x">{decodedDescription}</h2>
                        </div>
                        <p id="quantity-check" className="hc8-mt-3x">
                            {messages.quantityCheck(floorQty, backQty)}
                        </p>
                    </div>

                    <Modal
                        isOpen={isOpen}
                        headingText={decodedDescription}
                        onClose={() => setIsOpen(false)}
                    >
                        <div className="hc8-pa-3x">
                            <Grid.Container justify="center">
                                <Grid.Item>
                                    <Image
                                        src={`${base_url}${primary_image}`}
                                        alt={decodedDescription}
                                        height="148px"
                                    />
                                </Grid.Item>
                            </Grid.Container>
                        </div>
                    </Modal>

                    <CardSlider placements={placements} />
                    <Card className="hc8-fs-050 hc8-mt-3x hc8-mh-3x hc8-pa-3x hc8-pl-none">
                        <ItemSearchChart item_details={data.item_details} />
                    </Card>
                    <Card className="hc8-fs-050 hc8-mv-3x hc8-mh-3x hc8-pa-3x">
                        <div className="inventory-card">
                            <div>
                                <span>{t(onHandRow)}</span>
                                <span
                                    id="on-hand-quantity"
                                    className="hc8-ta-right"
                                >
                                    {onHandQty}
                                </span>
                            </div>
                            <div>
                                <span>{t(onWithdrawalRow)}</span>
                                <span
                                    id="on-withdrawal-quantity"
                                    className="hc8-ta-right"
                                >
                                    {onWayQty}
                                </span>
                            </div>
                            <div>
                                <span>{t(lastAuditedRow)}</span>
                                <span
                                    id="last-audited-date"
                                    className="hc8-ta-right"
                                >
                                    {formattedLastAuditedDate}
                                </span>
                            </div>
                            <div>
                                <span>{t(lastReceivedRow)}</span>
                                <span
                                    id="last-received-date"
                                    className="hc8-ta-right"
                                >
                                    {formattedLastReceivedDate}
                                </span>
                            </div>
                            <div>
                                <span>{t(lastSoldRow)}</span>
                                <span
                                    id="last-sold-date"
                                    className="hc8-ta-right"
                                >
                                    {formattedLastSoldDate}
                                </span>
                            </div>
                        </div>
                    </Card>
                    <Card className="hc8-fs-050 hc8-mv-3x hc8-mh-3x hc8-ph-3x">
                        <h3 id="total-sales-title" className="hc8-pv-3x">
                            {t(totalSalesCardTitle)}
                        </h3>
                        <div className="card-content hc8-pb-3x">
                            <div>
                                <span>{t(location)}</span>
                                <span>{t(unitsSold)}</span>
                                <span>{t(sales)}</span>
                            </div>
                            <div>
                                <span>{t(store)}</span>
                                <span>{store_sales_quantity}</span>
                                <span>
                                    {`$`}
                                    {store_sale_amount}
                                </span>
                            </div>
                            <div>
                                <span>{t(district)}</span>
                                <span id="district-total-sale-qty">
                                    {district_total_sale_quantity}
                                </span>
                                <span id="district-total-sale-amt">
                                    {`$`}
                                    {district_total_sale_amount}
                                </span>
                            </div>
                            <div>
                                <span>{t(company)}</span>
                                <span id="company-total-sale-qty">
                                    {company_total_sale_quantity}
                                </span>
                                <span id="company-total-sale-amt">
                                    {`$`}
                                    {company_total_sale_amount}
                                </span>
                            </div>
                        </div>
                    </Card>

                    <Card className="hc8-fs-050 hc8-mv-3x hc8-mh-3x hc8-ph-3x">
                        <h3 id="average-sales-title" className="hc8-pv-3x">
                            {t(averageSalesCardTitle)}
                        </h3>
                        <div className="card-content hc8-pb-3x">
                            <div>
                                <span>{t(location)}</span>
                                <span>{t(unitsSold)}</span>
                                <span>{t(sales)}</span>
                            </div>
                            <div>
                                <span>{t(district)}</span>
                                <span id="district-av-sale-qty">
                                    {district_average_quantity}
                                </span>
                                <span id="district-av-sale-amt">
                                    {`$`}
                                    {district_average_amount}
                                </span>
                            </div>
                            <div className="hc8-pb-3x">
                                <span>{t(company)}</span>
                                <span id="av-qty">
                                    {company_average_quantity}
                                </span>
                                <span id="av-amount">
                                    {`$`}
                                    {company_average_amount}
                                </span>
                            </div>
                        </div>
                    </Card>
                </Container>
            )}
        </>
    )
}
